import React from "react";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled, { css } from "styled-components";
import { TooltipHover } from "components/designV2/tooltip/tooltip";

/** Option definition for segment controller items. */
export interface SegmentOption<T extends string> {
  readonly value: T;
  readonly label: React.ReactNode;
  readonly iconName?: string;
  readonly disabledReason?: string;
}

/** Props for the segment controller component. */
export interface SegmentControllerProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  disabled?: boolean;
  label?: string;
  tone?: "default" | "dark";
  onChange: (value: T) => void;
}

const HEIGHT = 56;
const CONTAINER_PADDING_Y = 8;
const CONTAINER_PADDING_X = 16;
const CONTAINER_GAP = 16;

/** Horizontal segment controller with optional label and icons. */
export const SegmentController = <T extends string>({
  options,
  value,
  onChange,
  label,
  disabled = false,
  tone = "default",
}: SegmentControllerProps<T>): JSX.Element => {
  const handleSelect = (selectedValue: T, optionDisabled = false): void => {
    if (disabled || optionDisabled) return;
    onChange(selectedValue);
  };

  return (
    <Wrapper>
      {label && <Description>{label}</Description>}
      <Container role="tablist" aria-disabled={disabled} $tone={tone}>
        {options.map((option) => {
          const isSelected = option.value === value;
          const isDisabled = disabled || option.disabledReason != null;
          const segment = (
            <Segment
              type="button"
              role="tab"
              aria-selected={isSelected}
              $isSelected={isSelected}
              $tone={tone}
              onClick={() => handleSelect(option.value, isDisabled)}
              disabled={isDisabled}
            >
              {option.iconName && (
                <CustomIcon
                  name={option.iconName}
                  size="20px"
                  color={ColorV2.Blue1}
                />
              )}
              <Label>{option.label}</Label>
            </Segment>
          );

          return option.disabledReason ? (
            <TooltipHover key={option.value} text={option.disabledReason}>
              {segment}
            </TooltipHover>
          ) : (
            <React.Fragment key={option.value}>{segment}</React.Fragment>
          );
        })}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

const Description = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

const Container = styled.div<{ $tone: "default" | "dark" }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${CONTAINER_GAP}px;
  padding: ${CONTAINER_PADDING_Y}px ${CONTAINER_PADDING_X}px;
  height: ${HEIGHT}px;
  border-radius: 16px;
  background-color: ${(props) =>
    props.$tone === "dark" ? ColorV2.Blue6 : ColorV2.White};
`;

const Segment = styled.button<{
  $isSelected: boolean;
  $tone: "default" | "dark";
}>`
  ${TypographyV2.b1}
  display: inline-flex;
  align-items: center;
  gap: 12px;
  border: none;
  border-radius: 12px;
  padding: 8px 16px;
  background-color: transparent;
  color: ${ColorV2.Blue1};
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${ColorV2.Blue7};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:disabled:hover {
    background-color: transparent;
  }

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${props.$tone === "dark"
        ? ColorV2.White
        : ColorV2.Blue6};

      &:hover {
        background-color: ${props.$tone === "dark"
          ? ColorV2.White
          : ColorV2.Blue6};
      }
    `}
`;

const Label = styled.span`
  ${TypographyV2.b1}
  color: inherit;
`;
